import { AlertTriangle, Inbox } from "lucide-react";
import type { ReactNode } from "react";

import { Alert } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError, ContractViolationError } from "@/lib/api/fetcher";

interface QueryLike<T> {
    data: T | undefined;
    error: unknown;
    isPending: boolean;
}

interface QueryStateProps<T> {
    query: QueryLike<T>;
    isEmpty: (data: T) => boolean;
    emptyMessage: string;
    children: (data: T) => ReactNode;
}

/** One loading / error / empty / data rendering for every page. A 404 is "nothing found", not a failure. */
export function QueryState<T>({ query, isEmpty, emptyMessage, children }: QueryStateProps<T>) {
    if (query.error instanceof ApiError && query.error.status === 404) return <Empty message={emptyMessage} />;
    if (query.error) {
        return (
            <Alert variant="destructive">
                <AlertTriangle />
                <span>{describeError(query.error)}</span>
            </Alert>
        );
    }
    if (query.isPending || query.data === undefined) {
        return (
            <div className="space-y-2" aria-busy="true" aria-label="Loading">
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-2/3" />
            </div>
        );
    }
    if (isEmpty(query.data)) return <Empty message={emptyMessage} />;
    return children(query.data);
}

function Empty({ message }: { message: string }) {
    return (
        <Alert>
            <Inbox />
            <span className="text-muted-foreground">{message}</span>
        </Alert>
    );
}

export function describeError(error: unknown): string {
    if (error instanceof ContractViolationError) return error.message;
    if (error instanceof ApiError) return `Request failed: ${error.message}`;
    return error instanceof Error ? error.message : "Request failed";
}
